import { h, Fragment } from 'preact';
import { useMemo, useState, useEffect } from 'preact/hooks';
import { formatMoney } from 'utils/storefront/format-resource';
import Image from 'utils/image';
import PlusIcon from 'utils/icon/icon-plus.svg';
import MinusIcon from 'utils/icon/icon-minus.svg';
import clsx from 'clsx';
import { a11yLabel } from 'utils/a11y-label';
import { customizerEditUrl } from '../utils/customizer-experience';
import { withCartContext } from './cart-context';
import checkInventoryAvailable from './inventory-checker';
import checkForVariantUpdates from './variant-updates-checker';
import richTextToHTML from '../utils/rich-text-to-html';
import { getGiftCandidateState, setGiftCandidateState } from '../utils/gift-candidate-state';

const FREE_GIFT_ATTRIBUTE_KEY = '_free_gift_item';

const CartItem = (props) => {
  const { cart, line, index } = props;
  const [availableStock, setAvailableStock] = useState(Infinity);
  const [delayedShippingMessage, setDelayedShippingMessage] = useState(line.properties._delayedShippingMessage || '');

  useEffect(async () => {
    // checkForVariantUpdates now reads the PDP's own rendered HTML (no
    // Storefront API token involved), which has no metaobject/product
    // updatedAt timestamp to compare against — so staleness is judged by
    // whether the rendered message actually changed, not by comparing dates.
    const variantResponse = await checkForVariantUpdates(line.variant_id, line.url);
    const description = variantResponse?.delayedShippingMessage?.description;
    if (!description) return;

    const dsmHtml = richTextToHTML(description);
    if (dsmHtml === (line.properties._delayedShippingMessage || '')) return;

    await cart.updateAttributes({
      id: line.key,
      attributes: {
        ...line.properties,
        _delayedShippingMessage: dsmHtml,
        _cartLineItemUpdatedAt: new Date().toISOString(),
      },
    });
    setDelayedShippingMessage(dsmHtml);
  }, [line]);

  // Check inventory on component mount and when line quantity changes
  useEffect(() => {
    const checkInitialInventory = async () => {
      if (line.properties?._inventoryManagement !== 'shopify') {
        return;
      }

      const { maxQuantity: availableQuantity } = await checkInventoryAvailable(
        line.variant_id,
        line.quantity,
        cart,
        line.key,
      );

      setAvailableStock(availableQuantity);

      if (availableQuantity === 0) {
        const toastEvent = new CustomEvent('cart:notification', {
          detail: {
            message: `${line.product_title} is no longer available and will be removed from your cart.`,
            type: 'error',
            duration: 5000,
          },
        });
        document.dispatchEvent(toastEvent);
        cart.remove([line.key]);
        return;
      }
      if (availableQuantity < line.quantity) {
        const toastEvent = new CustomEvent('cart:notification', {
          detail: {
            message: `Only ${availableQuantity} ${line.product_title} are available. Quantity updated.`,
            type: 'warning',
            duration: 5000,
          },
        });
        document.dispatchEvent(toastEvent);

        cart.update([{ key: line.key, quantity: availableQuantity }]);
      }
    };

    checkInitialInventory();
  }, [line.variant_id, line.quantity]);

  const optionsNumber = line.options_with_values.filter((option) => option.value !== 'Default Title').length;

  const getAttributeValue = (targetLine, key) => {
    if (!targetLine) return null;
    if (targetLine.properties && Object.prototype.hasOwnProperty.call(targetLine.properties, key)) {
      return targetLine.properties[key];
    }
    if (Array.isArray(targetLine.attributes)) {
      const match = targetLine.attributes.find((attr) => (attr?.key || attr?.name) === key);
      if (match) return match.value;
    }
    return null;
  };

  const isFreeGiftLine = useMemo(() => {
    const value = getAttributeValue(line, FREE_GIFT_ATTRIBUTE_KEY);
    return value === true || value === 'true';
  }, [line]);

  const showFreeGiftPrice = isFreeGiftLine || isGift;

  const handleAdjustQuantity = async (quantity) => {
    if (isFreeGiftLine) return;
    await cart.update([{ key: line.key, quantity }]);
    setGiftCandidateState(getGiftCandidateState(), 'cart-item:quantity');
  };
  const handleRemove = async () => {
    const payload = [line.key];

    await cart.remove(payload);
    setGiftCandidateState(getGiftCandidateState(), 'cart-item:remove');

    // After removing, check if there's a gift wrap in cart
    const giftWrapItem = cart.state.items?.find((item) => item.properties._added_gift_wrap === 'true');

    if (giftWrapItem) {
      // Check remaining items, excluding both gift wrap and the item being removed
      const remainingItems = cart.state.items?.filter((item) => item.key !== giftWrapItem.key && item.key !== line.key);

      const hasEligibleItem = remainingItems?.some((item) => item.properties._gift_box_eligible === 'true');

      // If no eligible items remain, remove the gift wrap
      if (!hasEligibleItem) {
        await cart.remove([giftWrapItem.key]);
      }
    }
  };

  const isGift = useMemo(() => {
    if (line.properties._gwp) {
      return true;
    }
    return false;
  }, [props.line]);

  // A customizable product (SKU starts with C00) that is missing its design payload
  // (no `_isCustomProduct=true` flag and no `_customProductRecipeID__c`) cannot be
  // fulfilled. The Cart and Checkout Validation function blocks checkout server-side;
  // here we surface a storefront warning before the customer tries to check out.
  const isBrokenCustomizable = useMemo(() => {
    const sku = line.sku || line.merchandise?.sku || '';
    if (!sku.startsWith('C00')) return false;
    const lineProps = line.properties || {};
    const hasFlag = lineProps._isCustomProduct === 'true';
    const hasRecipe = (lineProps._customProductRecipeID__c || '').toString().length > 0;
    return !(hasFlag || hasRecipe);
  }, [line]);

  // line item link handling
  let lineItemHref = line.url;
  if (cart.mode === 'storefront') {
    lineItemHref = `/products/${line.merchandise.product.handle}`;
  }
  // Re-open the customizer to EDIT this line. Carry the FULL recipe context in the URL so the
  // page.customizer rebuild reconstructs it even cross-tab/cross-session, where the per-tab
  // sessionStorage handoff is empty (SHOPB2C-412). Lowercase `recipeId` — page.customizer reads
  // `recipeId`, and the prior `recipeID` never matched, so this edit link silently never
  // rehydrated. The rebuild overwrites the stored session from this URL, so every omitted field
  // is a field it blanks or defaults — customizerEditUrl carries the whole line-property context
  // (experience/gan/handle + supplemental personalization).
  const editUrl = customizerEditUrl(line);
  if (editUrl) lineItemHref = editUrl;

  // line item image handling
  let lineItemImage = line.children ? line.merchandise?.product?.image?.src : line.image;
  if (line.properties._isCustomProduct && line.properties._thumbnailUrl) {
    lineItemImage = line.properties._thumbnailUrl;
  }

  const lineItem = useMemo(
    () => (
      <div>
        <div
          key={line.id}
          className={clsx('px-15 py-28', index !== 0 && 'border-t border-grey-5')}
        >
          <div className="flex gap-15 font-franklin">
            <div className="flex w-[100px] items-center justify-center rounded border border-grey-5">
              {line.properties._isCustomProduct ? (
                <img
                  src={lineItemImage}
                  alt={`${line.product_title} image`}
                  className="block w-full object-contain"
                  width="150"
                  height="150"
                />
              ) : (
                <Image
                  src={lineItemImage}
                  classes="block object-contain w-full"
                  alt={`${line.product_title} image`}
                  width="150"
                  height="150"
                  srcWidths={{ sm: 200, md: 200 }}
                />
              )}
            </div>
            <div className="flex flex-1 flex-col gap-8">
              <a
                className={clsx(
                  'body-14-med text-faded-black',
                  line.product_type === 'Donation' && 'pointer-events-none',
                )}
                href={lineItemHref}
              >
                {line.children ? line.merchandise?.product?.title : line.product_title}
              </a>

              <div className="flex flex-wrap items-center gap-4">
                <div className="body-14 flex flex-wrap items-center gap-4">
                  {showFreeGiftPrice ? (
                    <span className="body-14 text-primary">{window.TMW?.Strings?.cart?.freeGift || 'Free Gift!'}</span>
                  ) : (
                    <>
                      {parseFloat(line.properties?._compare_at_price || 0) * 100 * line.quantity > line.line_price && (
                        <span className="line-through">
                          {formatMoney({
                            amount: parseFloat(line.properties?._compare_at_price || 0) * line.quantity,
                            currencyCode: window.TMW?.localization?.currencyCode,
                          })}
                        </span>
                      )}{' '}
                      {line.original_line_price > line.discounted_price * line.quantity &&
                        !line.properties?._compare_at_price && (
                          <span className="line-through">
                            {formatMoney({
                              amount: parseFloat(line.original_line_price / 100 || 0),
                              currencyCode: window.TMW?.localization?.currencyCode,
                            })}
                          </span>
                        )}
                      <span
                        className={
                          parseFloat(line.properties?._compare_at_price || 0) * 100 * line.quantity > line.line_price ||
                          line.original_line_price > line.discounted_price * line.quantity
                            ? 'text-error'
                            : ''
                        }
                      >
                        {line.original_line_price > line.discounted_price &&
                          formatMoney({
                            amount: (line.discounted_price * line.quantity) / 100,
                            currencyCode: window.TMW?.localization?.currencyCode,
                          })}
                        {line.original_line_price === line.discounted_price &&
                          formatMoney({
                            amount: line.line_price / 100,
                            currencyCode: window.TMW?.localization?.currencyCode,
                          })}
                      </span>
                    </>
                  )}
                </div>
                {isGift && (
                  <div className="body-12-med ml-2 rounded-full bg-surface-yellow px-10 py-5">
                    {window.TMW.Strings.cart.freeGift}
                  </div>
                )}
                {line.properties._promoBadgeBackgroundColor &&
                  line.properties._promoBadgeTextColor &&
                  line.properties._promoBadgeText && (
                    <div
                      className="body-12-med ml-2 rounded-full px-10 py-5"
                      style={{
                        backgroundColor: line.properties._promoBadgeBackgroundColor,
                        color: line.properties._promoBadgeTextColor,
                      }}
                    >
                      {line.properties._promoBadgeText}
                    </div>
                  )}
              </div>
              {line.discounts && line.discounts.length > 0 && !isFreeGiftLine && (
                <div className="flex flex-col gap-2">
                  {line.discounts.map((discount) => (
                    <span
                      key={discount.title}
                      className="leading-14 body-12-med w-fit rounded-full bg-[#ffdedf] px-12 py-4 text-black"
                    >
                      {discount.title} (-
                      {formatMoney({
                        amount: discount.amount / 100,
                        currencyCode: window.TMW?.localization?.currencyCode,
                      })}
                      )
                    </span>
                  ))}
                </div>
              )}

              {optionsNumber > 0 && (
                <ul className="m-0 flex list-none flex-wrap gap-4 p-0">
                  {line.options_with_values.map(
                    (option, i) =>
                      option.value !== 'Default Title' &&
                      line.product_type !== 'Donation' &&
                      line.product_title !== 'eGift Card' && (
                        <li
                          className="body-12-light m-0 flex gap-4 pb-4"
                          key={option.value}
                        >
                          <span>{option.value}</span>
                          {i < optionsNumber - 1 && <span>|</span>}
                        </li>
                      ),
                  )}
                </ul>
              )}

              {line.sku !== '' && line.product_type !== 'Donation' && line.product_title !== 'eGift Card' && (
                <p className="body-12-light">
                  {window.TMW.Strings.cart.item} #{line.sku}
                </p>
              )}

              {delayedShippingMessage && line.product_title !== 'eGift Card' && (
                <div
                  className="[&>p]:body-12-light [&>ol]:body-12-light [&>ul]:body-12-light [&>*>a]:body-12-light [&>*>a]:underline [&>p>a]:underline-offset-2"
                  dangerouslySetInnerHTML={{ __html: delayedShippingMessage }}
                ></div>
              )}

              {line.properties?._gift_box_eligible === 'false' && (
                <div className="body-12-light">This product makes your order ineligible for a gift box.</div>
              )}

              <div className={`flex items-end ${line.product_type === 'Donation' ? 'justify-end' : 'justify-between'}`}>
                {line.product_type !== 'Donation' && !isGift && line.sku !== 'GIFTWRAP00000' && !isFreeGiftLine && (
                  <div className="rounded-40 px-14 flex h-40 w-[95px] items-center rounded border border-faded-black py-8 text-14 leading-100">
                    <button
                      onClick={() => handleAdjustQuantity(line.quantity - 1)}
                      aria-label={a11yLabel('cart.remove')}
                      className={clsx(
                        'disabled:text-gray-2 flex h-full w-32 items-center justify-center focus:ring-offset-0',
                        line.quantity <= 1 ? 'text-grey-2' : 'text-faded-black',
                      )}
                      disabled={line.quantity <= 1}
                    >
                      <MinusIcon />
                    </button>
                    <input
                      type="number"
                      value={line.quantity}
                      onBlur={(e) => handleAdjustQuantity(parseInt(e.target.value, 10))}
                      max={availableStock}
                      aria-label={a11yLabel('product.quantityInput')}
                      className="flex h-32 w-28 items-center justify-center border-none p-0 text-center font-weight-medium text-faded-black"
                    />
                    <button
                      onClick={() => handleAdjustQuantity(line.quantity + 1)}
                      aria-label={a11yLabel('cart.add')}
                      className={clsx(
                        'disabled:text-gray-2 flex h-full w-32 items-center justify-center  focus:ring-offset-0',
                        line.quantity >= availableStock ? 'text-grey-2' : 'text-faded-black',
                      )}
                      disabled={line.quantity >= availableStock}
                    >
                      <PlusIcon />
                    </button>
                  </div>
                )}

                <button
                  className="body-12-light underline decoration-grey-3 hover:text-smoky-blue"
                  onClick={() => handleRemove()}
                >
                  {window.TMW.Strings.cart.remove}
                </button>
              </div>
            </div>
          </div>
          {isBrokenCustomizable && (
            <div
              role="alert"
              data-custom-missing-design
              className="cart-item__custom-warning mt-15 border-l-4 border-error bg-[#fdecec] px-15 py-12 text-faded-black"
            >
              <strong className="body-14-med text-error">This customizable item is missing its design.</strong>
              <p className="body-12-light mt-4 !leading-[18px]">
                The &quot;Buy again&quot; button doesn&apos;t include customizations. Open{' '}
                <a
                  className="body-12-light underline"
                  href="/account"
                >
                  your order history
                </a>{' '}
                and click &quot;View your design&quot; on the original order to reorder, or{' '}
                <a
                  className="body-12-light underline"
                  href={lineItemHref}
                >
                  design a new one
                </a>
                .
              </p>
            </div>
          )}
        </div>
      </div>
    ),
    [line, isGift, availableStock, isFreeGiftLine, showFreeGiftPrice, delayedShippingMessage, isBrokenCustomizable],
  );

  return lineItem;
};

export default withCartContext(CartItem);
